// SECURITY UTILITIES FOR QR CODES & CAMPAIGN MANAGEMENT
// Standalone utility functions for security validation and sanitization

// Note: For production, install DOMPurify: npm install dompurify @types/dompurify
// This file provides basic security utilities without external dependencies

// 1. DATA SANITIZATION
export const sanitizeInput = (input: string | null | undefined): string => {
  if (!input) return '';
  
  // Basic HTML/script sanitization (use DOMPurify in production)
  return input
    .toString()
    .trim()
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/javascript:/gi, '') // Remove javascript: URLs
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .replace(/[<>]/g, ''); // Remove angle brackets
};

export const validateUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  
  try {
    const urlObj = new URL(url);
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return null;
    }
    return url;
  } catch {
    return null;
  }
};

// 2. USER AUTHENTICATION
export const validateUserAuthentication = (user: any): boolean => {
  return !!(user?.id && user?.aud === 'authenticated');
};

/**
 * Get user role from database profile and metadata
 * Centralizes the logic for determining user roles
 *
 * @param user - Supabase user object
 * @param profile - Optional profile object from profiles table (for synchronous checks)
 */
export const getUserRole = (user: any, profile?: any): string => {
  if (!user) return 'guest';

  // Check profile is_admin field first (if provided)
  if (profile?.is_admin === true) return 'admin';

  // Check user_metadata and app_metadata for role
  const metadataRole = user.user_metadata?.role || user.app_metadata?.role;
  if (metadataRole) return metadataRole;

  // Check profile.user_metadata for is_admin flag (common pattern)
  if (user.user_metadata?.is_admin === true) return 'admin';
  if (user.app_metadata?.is_admin === true) return 'admin';

  return 'user';
};

/**
 * Check if user has admin privileges
 *
 * @param user - Supabase user object
 * @param profile - Optional profile object from profiles table (for synchronous checks)
 */
export const isAdmin = (user: any, profile?: any): boolean => {
  return getUserRole(user, profile) === 'admin';
};

/**
 * Check if user has admin privileges with database verification
 * This is more secure as it checks the actual database role
 */
export const isAdminFromDatabase = async (supabase: any, user: any): Promise<boolean> => {
  if (!user) return false;

  try {
    // A user is an admin if they are a superadmin (is_admin) OR they hold a role-based
    // admin role (admin_role_id). Checking is_admin alone silently rejected every
    // moderator/kerani with "Admin access required" even though their role granted the
    // section — the sidebar showed the page, then the hook refused to load data.
    const { data: profile } = await supabase
      .from('profiles')
      .select('is_admin, admin_role_id')
      .eq('id', user.id)
      .single();

    return profile?.is_admin === true || profile?.admin_role_id != null;
  } catch (error) {
    console.error('Error checking admin status:', error);
    // Not admin on error
    return false;
  }
};

// 3. SERVER-SIDE VALIDATION HELPERS
export const createOwnershipValidator = (supabase: any) => {
  const validateCampaignOwnership = async (campaignId: string, userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('user_id')
        .eq('id', campaignId)
        .eq('user_id', userId)
        .single();
      
      return !error && data?.user_id === userId;
    } catch {
      return false;
    }
  };

  const validateQRCodeOwnership = async (qrCodeId: string, userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('qr_codes')
        .select('user_id')
        .eq('id', qrCodeId)
        .eq('user_id', userId)
        .single();
      
      return !error && data?.user_id === userId;
    } catch {
      return false;
    }
  };

  return { validateCampaignOwnership, validateQRCodeOwnership };
};

// 4. SECURE LOGGING
export const createSecureLogger = () => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  return {
    info: (message: string, data?: any) => {
      if (isDevelopment) {
        console.info(message, data);
      }
    },
    warn: (message: string, data?: any) => {
      if (isDevelopment) {
        console.warn(message, data);
      }
    },
    error: (message: string, data?: any) => {
      if (isDevelopment) {
        console.error(message, data);
      } else {
        // Only log errors in production, without sensitive data
        console.error(message);
      }
    }
  };
};

// 5. DATA FILTERING HELPERS
export const filterUserData = <T extends { user_id?: string }>(
  data: T[], 
  userId: string
): T[] => {
  return data.filter(item => item.user_id === userId);
};

export const sanitizeQRCodeData = (qrCode: any): any => {
  if (!qrCode) return null;
  
  return {
    ...qrCode,
    title: sanitizeInput(qrCode.title || qrCode.name),
    cta: sanitizeInput(qrCode.cta),
    location: sanitizeInput(qrCode.location),
    industry: sanitizeInput(qrCode.industry),
    qr_code_url: validateUrl(qrCode.qr_code_url),
    destination_url: validateUrl(qrCode.destination_url),
    qr_image_url: validateUrl(qrCode.qr_image_url),
    scan_count: Math.max(0, Number(qrCode.scan_count) || 0)
  };
};

// 6. SECURITY VALIDATION RULES
export const SecurityRules = {
  // Check if user can access campaign
  canAccessCampaign: (campaign: any, userId: string): boolean => {
    return campaign?.user_id === userId;
  },
  
  // Check if user can access QR code
  canAccessQRCode: (qrCode: any, userId: string): boolean => {
    return qrCode?.user_id === userId;
  },
  
  // Validate data before processing
  isValidData: (data: any): boolean => {
    return data && typeof data === 'object';
  },
  
  // Check for suspicious patterns
  containsSuspiciousContent: (input: string): boolean => {
    const suspiciousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /data:text\/html/i,
      /vbscript:/i
    ];
    
    return suspiciousPatterns.some(pattern => pattern.test(input));
  }
};

// 7. ERROR HANDLING
export const SecurityError = {
  UNAUTHORIZED: 'Access denied: User not authenticated',
  FORBIDDEN: 'Access denied: Insufficient permissions',
  INVALID_DATA: 'Invalid or malicious data detected',
  OWNERSHIP_VIOLATION: 'Access denied: Resource not owned by user'
};

// 8. CONFIGURATION
export const SecurityConfig = {
  MAX_INPUT_LENGTH: 1000,
  ALLOWED_PROTOCOLS: ['http:', 'https:'],
  SANITIZATION_ENABLED: true,
  STRICT_VALIDATION: true
};
